import { useMemo } from "react";
import { Tooltip } from "antd";
import { domainRank } from "./medicalReview";
import type { ReviewItem } from "./medicalReview.types";

/**
 * The participant's reviewable records on one date axis, grouped by domain.
 *
 * Deliberately not the full `TimelineLibrary` used on the participant profile:
 * that one is driven by the `timeline` dataset through the dashboard harness
 * and assumes a full-page layout. Inside a 680px drawer what a reviewer needs
 * is the shape of the episode — what clustered around the record they are
 * looking at.
 */

const DOMAIN_COLOR: Record<string, string> = {
  eligibility: "#556C75",
  labs: "#2E7D5B",
  adverse_events: "#A31D5C",
  conmeds: "#06709B",
  ip_administration: "#A4720F",
  protocol_deviations: "#6B4E9E",
};

const FALLBACK_COLOR = "#8FA5A0";
const DAY_MS = 24 * 60 * 60 * 1000;
const AXIS_TICKS = 5;
const LABEL_WIDTH = "110px";

function formatTick(time: number, spansYears: boolean): string {
  return new Date(time).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(spansYears ? { year: "2-digit" } : {}),
  });
}

export default function ReviewContextTimeline({
  items,
  currentItemId,
}: {
  items: ReviewItem[];
  currentItemId?: number;
}) {
  const { rows, start, span, undatedCount, ticks } = useMemo(() => {
    const dated = items.filter((item) => item.event_date);
    const times = dated.map((item) => new Date(item.event_date!).getTime());
    const min = times.length ? Math.min(...times) : 0;
    const max = times.length ? Math.max(...times) : 0;
    // A single-day span would divide by zero and stack everything at 0%; pad it
    // to a day so one record sits mid-axis rather than on the edge.
    const width = Math.max(max - min, DAY_MS);

    const byDomain = new Map<string, ReviewItem[]>();
    for (const item of dated) {
      const bucket = byDomain.get(item.domain) ?? [];
      bucket.push(item);
      byDomain.set(item.domain, bucket);
    }

    const spansYears =
      new Date(min).getFullYear() !== new Date(max).getFullYear();

    return {
      rows: [...byDomain.entries()].sort(
        ([a], [b]) => domainRank(a) - domainRank(b) || a.localeCompare(b),
      ),
      start: min,
      span: width,
      undatedCount: items.length - dated.length,
      ticks: Array.from({ length: AXIS_TICKS }, (_, index) => {
        const fraction = index / (AXIS_TICKS - 1);
        return {
          fraction,
          label: formatTick(min + width * fraction, spansYears),
        };
      }),
    };
  }, [items]);

  if (rows.length === 0) {
    return (
      <p className="m-0 text-xs text-[#8FA5A0]">
        None of these records carries a clinical date, so there is no timeline
        to draw.
      </p>
    );
  }

  return (
    <section className="flex flex-col gap-1.5 rounded-lg border border-[#E6ECF1] bg-white px-3 py-2.5">
      <h4 className="m-0 text-[11px] font-semibold tracking-wider text-[#556C75] uppercase">
        Under review for this participant
      </h4>

      {rows.map(([domain, domainItems]) => (
        <div key={domain} className="flex items-center gap-2">
          <span
            className="shrink-0 truncate text-[11px] text-[#556C75]"
            style={{ width: LABEL_WIDTH }}
            title={domainItems[0].domain_label}
          >
            {domainItems[0].domain_label}
          </span>
          <div className="relative h-5 flex-1 rounded bg-[#F7F9FA]">
            {/* Gridlines share the axis fractions below, so a marker can be
                read against a date without tracing down the whole column. */}
            {ticks.map((tick) => (
              <span
                key={tick.fraction}
                aria-hidden
                className="absolute top-0 bottom-0 w-px bg-[#E6ECF1]"
                style={{ left: `${tick.fraction * 100}%` }}
              />
            ))}
            {domainItems.map((item) => {
              const offset =
                ((new Date(item.event_date!).getTime() - start) / span) * 100;
              const isCurrent = item.id === currentItemId;
              return (
                <Tooltip
                  key={item.id}
                  title={`${new Date(item.event_date!).toLocaleDateString()} — ${
                    item.summary || item.domain_label
                  }`}
                >
                  <span
                    aria-label={item.summary || item.domain_label}
                    className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                    style={{
                      left: `${offset}%`,
                      backgroundColor:
                        DOMAIN_COLOR[item.domain] ?? FALLBACK_COLOR,
                      outline: isCurrent ? "2px solid #2B4752" : undefined,
                      outlineOffset: isCurrent ? "1px" : undefined,
                    }}
                  />
                </Tooltip>
              );
            })}
          </div>
        </div>
      ))}

      {/* The axis. Offset by the same label column as the rows above so a tick
          sits under the point it dates. */}
      <div className="flex items-start gap-2">
        <span className="shrink-0" style={{ width: LABEL_WIDTH }} />
        <div className="relative h-6 flex-1 border-t border-[#E6ECF1]">
          {ticks.map((tick, index) => (
            <span
              key={tick.fraction}
              className="absolute top-0 flex flex-col items-start"
              style={{
                left: `${tick.fraction * 100}%`,
                // The end labels would overflow the container, so they hang
                // inward instead of centring on their tick.
                transform:
                  index === 0
                    ? "none"
                    : index === ticks.length - 1
                      ? "translateX(-100%)"
                      : "translateX(-50%)",
              }}
            >
              <span aria-hidden className="h-1 w-px bg-[#D6DEE3]" />
              <span className="mt-0.5 text-[10px] whitespace-nowrap text-[#8FA5A0]">
                {tick.label}
              </span>
            </span>
          ))}
        </div>
      </div>

      {undatedCount > 0 && (
        <p className="m-0 text-[11px] text-[#8FA5A0]">
          {undatedCount} record{undatedCount === 1 ? "" : "s"} carry no clinical
          date and are listed below only.
        </p>
      )}
    </section>
  );
}
